import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from "recharts";
import { GRADES, gradeLabel, canManage } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Camera, FileDown } from "lucide-react";

const RAG_COLORS = { red: "#EF4444", amber: "#F59E0B", green: "#10B981", blue_complete: "#3B82F6" };
const GRADE_COLORS = { urgent_improvement: "#DC2626", needs_attention: "#D97706", expected_standard: "#2563EB", strong_standard: "#059669", exceptional: "#4F46E5" };

export default function Analytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [snapOpen, setSnapOpen] = useState(false);
  const [label, setLabel] = useState("");

  const load = async () => {
    const [d, c] = await Promise.all([api.get("/dashboard/summary"), api.get("/cycles")]);
    setData(d.data); setCycles(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const snapshot = async () => {
    try { await api.post("/cycles", { label: label || `Cycle ${cycles.length + 1}`, notes: "" });
      toast.success("Cycle snapshot saved"); setSnapOpen(false); setLabel(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;

  const themeBars = data.themes.map((t) => {
    const s = data.by_theme[t.key];
    return { name: t.title.split(" ")[0], progress: s.avg_progress, actions: s.actions_total, evidence: s.evidence_total };
  });

  const ragTotals = { red: 0, amber: 0, green: 0, blue_complete: 0 };
  Object.values(data.by_theme).forEach((s) => Object.entries(s.rag_counts).forEach(([k, v]) => { ragTotals[k] += v; }));
  const ragPie = Object.entries(ragTotals).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v }));

  const gradeCounts = {};
  data.themes.forEach((t) => { gradeCounts[t.current_grade] = (gradeCounts[t.current_grade] || 0) + 1; });
  const gradePie = Object.entries(gradeCounts).map(([k, v]) => ({ name: gradeLabel(k), key: k, value: v }));

  const gradeScore = { urgent_improvement: 1, needs_attention: 2, expected_standard: 3, strong_standard: 4, exceptional: 5 };
  const trendData = cycles.map((c) => {
    const row = { name: c.label };
    c.snapshot.forEach((s) => { row[s.title.split(" ")[0]] = gradeScore[s.grade] || 0; });
    return row;
  });
  const trendKeys = data.themes.map((t) => t.title.split(" ")[0]);
  const trendColors = ["#312E81", "#059669", "#DC2626", "#D97706", "#2563EB", "#4F46E5", "#0F766E"];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-indigo-800">Analytics</div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>Coverage & progress</h1>
          <p className="text-slate-600 mt-1">Track where evidence is thin, where RAG needs to move and how grades map across the toolkit.</p>
        </div>
        {canManage(user?.role) && (
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="board-pack-btn" onClick={() => { const t = localStorage.getItem("qip_token"); window.open(`${api.defaults.baseURL}/board-pack/pdf?auth=${t}`, "_blank"); }} variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"><FileDown className="h-4 w-4 mr-1.5" />Governor board pack</Button>
            <Dialog open={snapOpen} onOpenChange={setSnapOpen}>
            <DialogTrigger asChild><Button data-testid="snapshot-cycle-btn" className="bg-indigo-900 hover:bg-indigo-950 text-white"><Camera className="h-4 w-4 mr-1.5" />Snapshot cycle</Button></DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle>Save review cycle snapshot</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                <div>Capture current grades, RAG counts and progress for every theme. Snapshots power the trend chart below.</div>
                <Input data-testid="cycle-label" placeholder={`Cycle ${cycles.length + 1}`} value={label} onChange={(e) => setLabel(e.target.value)} />
              </div>
              <DialogFooter><Button data-testid="save-cycle-btn" onClick={snapshot} className="bg-indigo-900 hover:bg-indigo-950 text-white">Save snapshot</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {cycles.length >= 1 && (
        <Card className="p-5 border-slate-200">
          <h3 className="font-bold text-slate-900 mb-1" style={{ fontFamily: "Plus Jakarta Sans" }}>Grade trend across cycles</h3>
          <p className="text-xs text-slate-500 mb-4">1 = Urgent Improvement · 5 = Exceptional · {cycles.length} snapshot{cycles.length === 1 ? "" : "s"}</p>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {trendKeys.map((k, i) => (<Line key={k} type="monotone" dataKey={k} stroke={trendColors[i % trendColors.length]} strokeWidth={2} />))}
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5 border-slate-200">
          <h3 className="font-bold text-slate-900 mb-4" style={{ fontFamily: "Plus Jakarta Sans" }}>Average action progress by theme</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={themeBars}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="progress" fill="#312E81" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 border-slate-200">
          <h3 className="font-bold text-slate-900 mb-4" style={{ fontFamily: "Plus Jakarta Sans" }}>Evidence coverage by theme</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={themeBars}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="evidence" fill="#059669" radius={[6, 6, 0, 0]} />
              <Bar dataKey="actions" fill="#4338CA" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 border-slate-200">
          <h3 className="font-bold text-slate-900 mb-4" style={{ fontFamily: "Plus Jakarta Sans" }}>Action RAG distribution</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={ragPie} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} label>
                {ragPie.map((e) => (<Cell key={e.name} fill={RAG_COLORS[e.name]} />))}
              </Pie>
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 border-slate-200">
          <h3 className="font-bold text-slate-900 mb-4" style={{ fontFamily: "Plus Jakarta Sans" }}>Current grade profile</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={gradePie} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} label>
                {gradePie.map((e) => (<Cell key={e.key} fill={GRADE_COLORS[e.key]} />))}
              </Pie>
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-5 border-slate-200">
        <h3 className="font-bold text-slate-900 mb-4" style={{ fontFamily: "Plus Jakarta Sans" }}>Grade heatmap</h3>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">Theme</th>
                {GRADES.map((g) => <th key={g.key} className="py-2 px-3 text-center">{g.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.themes.map((t) => (
                <tr key={t.key} className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-medium text-slate-900">{t.title}</td>
                  {GRADES.map((g) => (
                    <td key={g.key} className="py-3 px-3 text-center">
                      {t.current_grade === g.key ? <span className={`inline-block h-3 w-3 rounded-full`} style={{ background: GRADE_COLORS[g.key] }} /> : <span className="inline-block h-3 w-3 rounded-full bg-slate-100" />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
